import React from 'react';
import { Icon, IconName } from 'app/common/components/controls';
import styles from './AUAAPhase2Banner.less';
import { useFeatureFlag } from '@trello/feature-flag-client';
import { OrganizationFeatures } from '@trello/product-features';
import { forTemplate } from 'app/common/lib/util/i18n';
import {
  useAllAdminsBannerQuery,
  useDismissAllAdminsBannerMutation,
} from '@trello/graphql/generated';
import { useFreeTrialEligibilityRules } from 'app/src/components/FreeTrial/useFreeTrialEligibilityRules';

const format = forTemplate('auaa_phase2_banner');

interface BannerProps {
  orgNameOrId: string;
}

interface OrgMembership {
  idMember: string;
  memberType: string;
  deactivated: boolean;
}

const isAdminMembership = (membership: OrgMembership) =>
  membership.memberType === 'admin' && !membership.deactivated;

export const AUAAPhase2Banner: React.FC<BannerProps> = ({ orgNameOrId }) => {
  const { data, loading } = useAllAdminsBannerQuery({
    variables: { orgNameOrId },
  });
  const {
    hasFreeTrialCredit,
    loading: freeTrialCreditLoading,
  } = useFreeTrialEligibilityRules(orgNameOrId);
  const [dismiss] = useDismissAllAdminsBannerMutation();

  const flagIsOn = useFeatureFlag(
    'btg.all-users-are-admins.phase-2.banner',
    false,
  );

  if (
    !flagIsOn ||
    loading ||
    !data?.member ||
    !data.organization ||
    data.organization.allAdminsEnabled ||
    hasFreeTrialCredit ||
    freeTrialCreditLoading ||
    OrganizationFeatures.hasProduct(data.organization.products[0])
  ) {
    return null;
  }

  const hasDismissed = data.member.oneTimeMessagesDismissed?.some(
    (dismissed) => dismissed === `auaa-phase2-banner-${data.organization?.id}`,
  );
  if (hasDismissed) {
    return null;
  }

  const isAdmin = data.organization.memberships?.some(
    (m) => m.idMember === data.member?.id && isAdminMembership(m),
  );
  if (!isAdmin) {
    return null;
  }

  const onDismiss = () => {
    dismiss({
      variables: {
        messageId: `auaa-phase2-banner-${data.organization?.id}`,
      },
    });
  };

  return (
    <div className={styles.banner}>
      <div className={styles.content}>
        <strong className={styles.headline}>
          <span aria-hidden="true" role="presentation">
            👋{' '}
          </span>
          {format('title-30')}
        </strong>
        {format('body')}
      </div>
      <a
        href="https://help.trello.com/article/1215-team-admin-permissions"
        target="_blank"
        className={styles.learnMore}
      >
        {format('learn-more')}
      </a>
      <button
        className={styles.close}
        aria-label="Close banner"
        onClick={onDismiss}
      >
        <Icon name={IconName.Close} medium />
      </button>
    </div>
  );
};
